// logic with direct authentication attempt over same connection to not probe first and consume auth_key
import React, {
  KeyboardEvent,
  MouseEvent,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { ScanLoadingLinearIndicator } from "@src/components/molecules/scan-loading-linear-indicator/scan-loading-linear-indicator";
import { i18n } from "@src/i18n/i18n";
import { Language } from "@src/reducers/your-reducer";
import { Button } from "@src/components/atoms/buttons/button/button";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import { useLocation, useParams } from "react-router-dom";
import {
  errorSnackbar,
  successSnackbar,
  warningSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import {
  loadWorkingNoVnc,
  NoVncVersion,
} from "@src/cloud-connect/vnc/utils/novnc-loader";
import styles from "./vnc-page.module.scss";
import smallTomorrowThingsLogo from "@src/assets/svg/small-tt-logo.svg";
import companyIconGray from "@src/assets/svg/company-icon-gray.svg";
import sourceIcon from "@src/assets/svg/source-icon.svg";
import { VncCountdownBar } from "@src/components/atoms/countdown-bar/vnc-countdown-bar";
import { ButtonWithIcon } from "@src/components/atoms/buttons/button-with-icon/button-with-icon";
import disconnectSessionOrangeIcon from "@src/assets/svg/disconnect-session-orange-icon.svg";
import bigMachineVector from "@src/assets/svg/big-machine-vector.svg";
import { IconButton } from "@src/components/atoms/buttons/icon-button/icon-button";
import closedPasswordEye from "@src/assets/svg/password-eye-closed-icon.svg";
import openPasswordEye from "@src/assets/svg/password-eye-open-icon.svg";

enum VncStatus {
  NOT_STARTED,
  CONNECTING,
  CONNECTED,
  DISCONNECTED,
  ERROR,
  AUTH_REQUIRED,
  AUTH_FAILED,
  SESSION_TERMINATED,
}

const MAX_RETRY_ATTEMPTS = 3;

const cssClassName = "vnc-page";

const formatText = (
  template: string,
  vars: Record<string, string | null | undefined> = {},
): string => template.replace(/\{(\w+)\}/g, (_match, key) => vars[key] ?? "");

export const VncPage = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const location = useLocation();

  const containerRef = useRef<HTMLDivElement>(null);
  const rfbRef = useRef<any>(null);
  const wasConnectedRef = useRef(false);
  const retryAttempts = useRef(0);
  const readyIdRef = useRef<string | undefined>(undefined);
  if (!readyIdRef.current) {
    readyIdRef.current =
      window.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
  }

  const [wsUrl, setWsUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<VncStatus>(VncStatus.NOT_STARTED);
  const [statusMessage, setStatusMessage] = useState("");
  const [password, setPassword] = useState("");
  const [showText, setShowText] = useState<boolean>(false);

  const searchParams = new URLSearchParams(location.search);
  const companyName = searchParams.get("companyName");
  const machineId = searchParams.get("machineId");
  const machineName =
    searchParams.get("machineName") ||
    searchParams.get("sourceName") ||
    machineId;
  const machineStatus =
    searchParams.get("machineStatus") || searchParams.get("sourceStatus");
  const duration = searchParams.get("duration");
  const language = searchParams.get("language") as Language;
  const namespace = searchParams.get("namespace");
  const novncVersion: NoVncVersion =
    searchParams.get("novncVersion") === "1.6.0" ? "1.6.0" : "1.3.0";
  const swapRB = searchParams.get("swapRB") === "true";
  const authFailedRef = useRef(false);
  const texts = {
    ttLogoAltText: i18n("pages.vncpage.tomorrowThingsLogoAltText", language),
    disconnectButton: i18n("pages.vncpage.disconnectButton", language),
    submitButtonText: i18n("pages.vncpage.submitButtonText", language),
    enterVncPasswordPlaceholder: i18n(
      "pages.vncpage.enterVncPasswordPlaceholder",
      language,
    ),

    connectingVNCText: i18n("pages.vncpage.connectingVNCText", language),
    authenticationRequiredText: i18n(
      "pages.vncpage.authenticationRequiredText",
      language,
    ),
    failedToConnectVncText: i18n(
      "pages.vncpage.failedToConnectVncText",
      language,
    ),
    connectedToSourceText: formatText(
      i18n("pages.vncpage.connectedToSourceText", language),
      { sourceName: machineName, companyName },
    ),
    disconnectedFromSourceText: i18n(
      "pages.vncpage.disconnectedFromSourceText",
      language,
    ),
    authenticationFailedText: i18n(
      "pages.vncpage.authenticationFailedText",
      language,
    ),
    authenticatingText: i18n("pages.vncpage.authenticatingText", language),
    authenticationFailedWithAllVersionsText: i18n(
      "pages.vncpage.authenticationFailedWithAllVersionsText",
      language,
    ),
    companyIconAltText: i18n("pages.vncpage.companyIconAltText", language),
    machineAltText: i18n("pages.vncpage.machineAltText", language),
    sourceIconAltText: i18n("pages.vncpage.sourceIconAltText", language),
    testingAuthenticationText: i18n(
      "pages.vncpage.testingAuthenticationText",
      language,
    ),
    sessionTerminatedText: i18n(
      "pages.vncpage.sessionTerminatedText",
      language,
    ),
  };
  const disconnectVncAndCloseTab = () => {
    try {
      rfbRef.current?.disconnect();
    } catch (e) {
      console.error("Error disconnecting VNC:", e);
    }
    window.close();
  };

  const notifyAppOfClosedConnection = () => {
    if (!wasConnectedRef.current) return;
    try {
      window.opener?.postMessage(
        { type: "VNC_CLOSED", sessionId },
        window.location.origin,
      );
      wasConnectedRef.current = false;
    } catch (e) {
      console.error("Error notifying app of closed VNC connection:", e);
    }
  };

  // get the wsUrl from the opener window via postMessage
  useLayoutEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin || e.source !== window.opener)
        return;

      // opener could not retrieve an auth key: session is gone, do not retry
      if (
        e.data?.type === "VNC_SESSION_ENDED" &&
        e.data.sessionId === sessionId
      ) {
        console.warn("[VNC] Session ended by opener, no key available");
        setStatus(VncStatus.ERROR);
        setStatusMessage(texts.sessionTerminatedText);
        return;
      }

      if (e.data?.type === "VNC_KEY" && e.data.sessionId === sessionId) {
        setWsUrl(e.data.wsUrl);
      }
    };

    window.addEventListener("message", handleMessage);

    window.opener?.postMessage(
      {
        type: "VNC_TAB_READY",
        sessionId,
        namespace,
        readyId: readyIdRef.current,
      },
      window.location.origin,
    );

    if (!window.opener) {
      console.warn("[VNC] VNC_TAB_READY not sent: opener is missing", {
        sessionId,
      });
    }
    return () => window.removeEventListener("message", handleMessage);
  }, [namespace, sessionId, texts.sessionTerminatedText]);

  useEffect(() => {
    if (wsUrl) return;

    const timeout = window.setTimeout(() => {
      console.warn("[VNC] VNC_KEY not received within timeout", {
        sessionId,
      });
    }, 7000);

    return () => window.clearTimeout(timeout);
  }, [wsUrl, sessionId]);

  // notify the opener window that connection failed so it can retry the auth key
  const notifyAppOfConnectionFailure = () => {
    console.log(
      "[VNC] Notifying parent of connection failure (non-auth related)",
    );
    try {
      window.opener?.postMessage(
        {
          type: "CONNECTION_FAILED",
          sessionId,
          namespace,
        },
        window.location.origin,
      );
    } catch (e) {
      console.error("Error notifying app of connection failure:", e);
    }
  };

  useEffect(() => {
    const handleRetryMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin || e.source !== window.opener)
        return;
      if (
        e.data?.type === "VNC_RETRY" &&
        e.data.sessionId === sessionId &&
        status != VncStatus.ERROR
      ) {
        console.log(
          "[VNC] Received retry message with new auth key for session",
        );

        // clean up existing connection
        if (rfbRef.current) {
          rfbRef.current = null;
        }

        // reset connection state but KEEP password
        wasConnectedRef.current = false;
        authFailedRef.current = false;
        authFailedRef.current = false;

        if (retryAttempts.current > MAX_RETRY_ATTEMPTS) {
          console.error(
            `Exceeded max retry attempts (${MAX_RETRY_ATTEMPTS}). Not retrying further.`,
          );
          setStatus(VncStatus.ERROR);
          setStatusMessage(texts.authenticationFailedWithAllVersionsText);
          errorSnackbar(texts.authenticationFailedWithAllVersionsText);
          return;
        } else {
          retryAttempts.current += 1;
          setWsUrl(e.data.wsUrl); // set new wsURL including new auth key to trigger re-connect via useEffect
        }
      }
    };

    window.addEventListener("message", handleRetryMessage);
    return () => window.removeEventListener("message", handleRetryMessage);
  }, [sessionId, texts.authenticationFailedWithAllVersionsText]);

  // single VNC connection attempt with authentication
  useEffect(() => {
    if (!wsUrl || !containerRef.current) return;
    if (rfbRef.current) return;

    let isCancelled = false;

    const connect = async () => {
      try {
        setStatus(VncStatus.CONNECTING);
        setStatusMessage(texts.connectingVNCText);

        const { RFBClass } = await loadWorkingNoVnc(novncVersion);

        RFBClass.setLogging?.("debug");

        if (isCancelled) return;

        const rfb = new RFBClass(containerRef.current, wsUrl, {
          scaleViewport: true,
          resizeSession: false,
          /*showDotCursor: true,  */
          qualityLevel: 2,
          compressionLevel: 2,
          preferredEncoding: "tight",
          /* this can be set to false, much lower quality will be there but performance will improve */
          trueColor: false,
        });

        rfbRef.current = rfb;

        // apparently needed to be declared after initialization to really show the dot cursor
        // keep as it is until we introduce 1.6 as well
        rfb.showDotCursor = true;

        rfb.swapRB = swapRB;

        rfb.addEventListener("credentialsrequired", () => {
          setStatus(VncStatus.AUTH_REQUIRED);
          setStatusMessage(texts.authenticationRequiredText);
        });

        rfb.addEventListener("connect", () => {
          wasConnectedRef.current = true;
          window.opener?.postMessage(
            { type: "VNC_CONNECTION_ESTABLISHED", sessionId },
            window.location.origin,
          );
          setStatus(VncStatus.CONNECTED);
          setStatusMessage("");
          window.dispatchEvent(new Event("resize"));
          successSnackbar(
            formatText(texts.connectedToSourceText, {
              sourceName: machineName,
              companyName,
            }),
          );
        });

        rfb.addEventListener("securityfailure", () => {
          authFailedRef.current = true;
          setStatus(VncStatus.AUTH_FAILED);
          setStatusMessage(texts.authenticationFailedText);
          warningSnackbar(texts.authenticationFailedText);
        });

        rfb.addEventListener("disconnect", () => {
          if (authFailedRef.current) return;

          // was connected then got disconnected, session was terminated
          if (wasConnectedRef.current) {
            setStatus(VncStatus.SESSION_TERMINATED);
            setStatusMessage(texts.sessionTerminatedText);
            return;
          }

          // never connected, connection setup failure, retry if possible
          if (
            retryAttempts.current < MAX_RETRY_ATTEMPTS &&
            status !== VncStatus.ERROR
          ) {
            notifyAppOfConnectionFailure();
            return;
          }

          // retries exhausted
          setStatus(VncStatus.ERROR);
          setStatusMessage(texts.failedToConnectVncText);
          errorSnackbar(texts.failedToConnectVncText);
        });
      } catch (err) {
        if (isCancelled) return;
        setStatus(VncStatus.ERROR);
        setStatusMessage(
          err instanceof Error ? err.message : texts.failedToConnectVncText,
        );
        errorSnackbar(
          err instanceof Error ? err.message : texts.failedToConnectVncText,
        );
      }
    };

    connect();

    return () => {
      isCancelled = true;
    };
  }, [wsUrl]);

  // password submission (NO reconnect)

  const submitPassword = () => {
    if (!password || !rfbRef.current) return;

    setStatus(VncStatus.CONNECTING);
    setStatusMessage(texts.authenticatingText);

    rfbRef.current.sendCredentials({ password });
  };

  // resize handling

  useEffect(() => {
    const onResize = () => {
      if (rfbRef.current) {
        rfbRef.current.scaleViewport = true;
        // rfbRef.current._updateScale(); // optional if needed
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // listen for SSE interrupt via BroadcastChannel

  useEffect(() => {
    const channel = new BroadcastChannel("vnc-interrupt");

    channel.onmessage = (event: MessageEvent) => {
      const { type, sessionId: interruptedSessionId } = event.data || {};
      if (type === "VNC_INTERRUPT" && interruptedSessionId === sessionId) {
        console.log("[VNC] Received interrupt for session:", sessionId);
        try {
          rfbRef.current?.disconnect();
        } catch (e) {
          console.error("Error disconnecting VNC on interrupt:", e);
        }
      }
    };

    return () => channel.close();
  }, [sessionId]);

  // cleanup

  useLayoutEffect(() => {
    return () => {
      try {
        rfbRef.current?.disconnect();
      } catch (e) {
        console.log("Cleanup disconnect error:", e);
      }
    };
  }, []);

  useLayoutEffect(() => {
    const handleBeforeUnload = () => {
      if (wasConnectedRef.current) {
        notifyAppOfClosedConnection();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [sessionId]);

  const handlePasswordButton = (
    e: MouseEvent<Element> | KeyboardEvent<Element>,
  ) => {
    e.preventDefault();
    setShowText(!showText);
  };

  return (
    <div className={styles[cssClassName]}>
      <div className={styles[`${cssClassName}__header`]}>
        <div className={styles[`${cssClassName}__left-wrapper`]}>
          <img src={smallTomorrowThingsLogo} alt={texts.ttLogoAltText} />
          <div
            className={
              styles[`${cssClassName}__company-source-name-status-wrapper`]
            }
          >
            <img src={companyIconGray} alt={texts.companyIconAltText} />
            <span className={styles[`${cssClassName}__company-name-text`]}>
              {companyName}
            </span>
            <div className={styles[`${cssClassName}__separator`]}>/</div>
            {/* <IndicatorText
              variant={machineStatus === "CONNECTED" ? "green" : "red"}
              text=""
            /> */}
            <span className={styles[`${cssClassName}__source-name-text`]}>
              {machineName}
            </span>
            <img src={sourceIcon} alt={texts.sourceIconAltText} />
          </div>
        </div>
        <div className={styles[`${cssClassName}__center-wrapper`]}>
          {duration && <VncCountdownBar duration={duration} />}
        </div>

        <div className={styles[`${cssClassName}__right-wrapper`]}>
          <ButtonWithIcon
            fun={disconnectVncAndCloseTab}
            icon={disconnectSessionOrangeIcon}
            text={texts.disconnectButton}
            variant="warning-with-outline"
          />
        </div>
      </div>

      {status === VncStatus.AUTH_REQUIRED && (
        <div className={styles[`${cssClassName}__loading-overlay`]}>
          <div className={styles[`${cssClassName}__outer-password-wrapper`]}>
            <p className={styles[`${cssClassName}__loading-text`]}>
              {statusMessage}
            </p>
            <FlexRowContainer>
              <div
                className={styles[`${cssClassName}__password-input-wrapper`]}
              >
                <input
                  className={styles[`${cssClassName}__password-input`]}
                  placeholder={texts.enterVncPasswordPlaceholder}
                  value={password}
                  type={showText ? "text" : "password"}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && submitPassword()}
                />
                <div className={styles[`${cssClassName}__password-button`]}>
                  <IconButton
                    fun={(e) => handlePasswordButton(e)}
                    icon={showText ? closedPasswordEye : openPasswordEye}
                  />
                </div>
              </div>
              <Button fun={submitPassword} text={texts.submitButtonText} />
            </FlexRowContainer>
          </div>
        </div>
      )}

      {(status === VncStatus.CONNECTING ||
        status === VncStatus.DISCONNECTED ||
        status === VncStatus.AUTH_FAILED ||
        status === VncStatus.ERROR ||
        status === VncStatus.SESSION_TERMINATED) && (
        <div className={styles[`${cssClassName}__loading-overlay`]}>
          <div className={styles[`${cssClassName}__loading-wrapper`]}>
            <img src={bigMachineVector} alt={texts.machineAltText} />

            <div className={styles[`${cssClassName}__loading-scan-wrapper`]}>
              {status === VncStatus.CONNECTING && (
                <ScanLoadingLinearIndicator />
              )}
            </div>
            <p className={styles[`${cssClassName}__loading-text`]}>
              {statusMessage}
            </p>
          </div>
        </div>
      )}
      <div
        ref={containerRef}
        className={styles[`${cssClassName}__vnc-container`]}
      />
    </div>
  );
};
